import { importacionOperacionesApi } from "../importacion-operaciones/importacionOperacionesApi";

const API_ROOT = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const PAGOS_API_BASE = `${API_ROOT}/api/pagos`;

// Campos del preview payment-engine que necesitan los controles previos. Se envía sólo este
// subconjunto: el control no recalcula importes de la corrida.
const SNAPSHOT_FIELDS = [
  "fiservOperationId", "terminalFiserv", "merchantId", "matchStatus", "resolutionStatus",
  "paymentEligibility", "scheduleType", "requiresCommercialExceptionRule", "paymentAmountStatus", "grossAmount",
];

export const toRunSnapshot = (rows = []) => rows.map((row) => Object.fromEntries(
  SNAPSHOT_FIELDS.map((field) => [field, row[field] ?? null]),
));

const buildForm = ({ fur, txt, runId, runRows, pagosManuales }) => {
  const formData = new FormData();
  formData.append("fur", fur);
  if (txt) formData.append("txt", txt);
  if (runId && runRows) {
    formData.append("runId", runId);
    formData.append("operacionesCorrida", JSON.stringify(toRunSnapshot(runRows)));
  }
  formData.append("pagosManuales", JSON.stringify(pagosManuales || []));
  return formData;
};

const buildProcesamientoForm = ({ importacionId, runId, txt, pagosManuales }) => {
  const formData = new FormData();
  formData.append("importacionId", importacionId);
  if (runId) formData.append("runId", runId);
  if (txt) formData.append("txt", txt);
  formData.append("pagosManuales", JSON.stringify(pagosManuales || []));
  return formData;
};

const request = (path, token, options = {}) => importacionOperacionesApi.request(`${PAGOS_API_BASE}${path}`, {
  cache: "no-store",
  ...options,
  headers: { ...importacionOperacionesApi.buildHeaders(token), ...(options.headers || {}) },
});

export const pagosApi = {
  async analizarControlesPrevios(payload, token) {
    return request("/controles-previos/analizar", token, { method: "POST", body: buildForm(payload) });
  },

  // Liquidaciones persistidas: sólo responden en el entorno de desarrollo aislado.
  async estadoLiquidaciones(token) {
    return request("/liquidaciones/estado", token);
  },

  async registrarLiquidacion({ fechaPago, ...payload }, token) {
    const formData = buildForm(payload);
    formData.append("fechaPago", fechaPago);
    return request("/liquidaciones", token, { method: "POST", body: formData });
  },

  async obtenerPendientes(token) {
    return request("/liquidaciones/obligaciones/pendientes?canal=SAS", token);
  },

  // Procesamiento SAS: lote = importación de Importación Ops + corrida de Conciliación Ops.
  async lotesSas(fecha, token) {
    return request(`/procesamiento/lotes?fecha=${encodeURIComponent(fecha)}`, token);
  },

  async procesarSas(payload, token) {
    return request("/procesamiento/controles", token, { method: "POST", body: buildProcesamientoForm(payload) });
  },

  async registrarProcesamiento(payload, token) {
    return request("/procesamiento/registrar", token, { method: "POST", body: buildProcesamientoForm(payload) });
  },

  async informarPagoManual(obligacionId, body, token) {
    return request(`/liquidaciones/obligaciones/${obligacionId}/pagos-manuales`, token, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  },

  // Condiciones financieras (catálogo de pruebas en ZocoPagos_Dev). El porcentaje viaja como
  // texto y la simulación sólo recibe IDs: los importes se calculan en backend.
  async estadoCondiciones(token) {
    return request("/condiciones/estado", token);
  },

  async snapshotsCondiciones(token) {
    return request("/condiciones/snapshots", token);
  },

  async cargarSnapshotCondiciones(libro, token) {
    const formData = new FormData();
    formData.append("libro", libro);
    return request("/condiciones/snapshots", token, { method: "POST", body: formData });
  },

  async buscarComercios(texto, token) {
    return request(`/condiciones/comercios?q=${encodeURIComponent(texto)}`, token);
  },

  async condicionComercio(cuit, snapshotId, token) {
    const query = snapshotId ? `?snapshotId=${encodeURIComponent(snapshotId)}` : "";
    return request(`/condiciones/comercios/${encodeURIComponent(cuit)}/condicion${query}`, token);
  },

  async listarReglas({ estado, sujeto } = {}, token) {
    const params = new URLSearchParams();
    if (estado) params.append("estado", estado);
    if (sujeto) params.append("sujeto", sujeto);
    const query = params.toString();
    return request(`/condiciones/reglas${query ? `?${query}` : ""}`, token);
  },

  async obtenerRegla(id, token) {
    return request(`/condiciones/reglas/${id}`, token);
  },

  async historialRegla(raizId, token) {
    return request(`/condiciones/reglas/${raizId}/historial`, token);
  },

  async crearRegla(body, token) {
    return request("/condiciones/reglas", token, json("POST", body));
  },

  async editarRegla(id, body, token) {
    return request(`/condiciones/reglas/${id}`, token, json("PUT", body));
  },

  async nuevaVersionRegla(raizId, body, token) {
    return request(`/condiciones/reglas/${raizId}/versiones`, token, json("POST", body));
  },

  async simularRegla(id, { importacionId, snapshotId }, token) {
    return request(`/condiciones/reglas/${id}/simular`, token, json("POST", { importacionId, snapshotId }));
  },

  async enviarRegla(id, token) {
    return request(`/condiciones/reglas/${id}/enviar`, token, { method: "POST" });
  },

  async aprobarRegla(id, comentario, token) {
    return request(`/condiciones/reglas/${id}/aprobar`, token, json("POST", { comentario }));
  },

  async rechazarRegla(id, comentario, token) {
    return request(`/condiciones/reglas/${id}/rechazar`, token, json("POST", { comentario }));
  },

  // FUR autónomo (simulación). Se envían IDs y, opcionalmente, el Diario para comparar
  // componentes; los netos y grupos se calculan en backend.
  async furCorridas(fecha, token) {
    return request(`/fur-autonomo/corridas${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ""}`, token);
  },

  async furResultado(id, token) {
    return request(`/fur-autonomo/corridas/${id}`, token);
  },

  // Liquidación SAS desde archivos originales (ZIP/PDF Fiserv). Corrida temporal en ZocoPagos_Dev.
  async archivosCorridas(fecha, token) {
    return request(`/archivos/corridas${fecha ? `?fecha=${encodeURIComponent(fecha)}` : ""}`, token);
  },

  async archivosResultado(id, token) {
    return request(`/archivos/corridas/${id}`, token);
  },

  async archivosAuditarFechas(archivos, token) {
    const formData = new FormData();
    (archivos || []).forEach((archivo) => formData.append("archivos", archivo));
    return request("/archivos/auditoria-fechas", token, { method: "POST", body: formData });
  },

  async archivosProcesar({ fechaPago, snapshotId, archivos, resolverParametrosAutomaticamente = false }, token) {
    const formData = new FormData();
    formData.append("fechaPago", fechaPago);
    formData.append("canal", "SAS");
    if (snapshotId) formData.append("snapshotId", snapshotId);
    formData.append("resolverParametrosAutomaticamente", String(resolverParametrosAutomaticamente));
    (archivos || []).forEach((archivo) => formData.append("archivos", archivo));
    return request("/archivos/corridas", token, { method: "POST", body: formData });
  },

  // Anticipos y excepciones sobre una corrida desde archivos. Se envían IDs y el porcentaje como
  // texto; netos, diferencias y FUR los calcula el backend.
  async anticiposPanel(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}`, token);
  },

  async anticiposProponer(corridaId, body, token) {
    return request(`/anticipos/corridas/${corridaId}/propuestas`, token, json("POST", body));
  },

  async anticiposRecalcularCorrida(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}/recalcular`, token, json("POST", {}));
  },

  async anticiposSimularHipotesisHistorica(corridaId, referenciaIds, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis-historica`, token, json("POST", { referenciaIds }));
  },

  // Decisiones pendientes de la corrida con lo que hizo el histórico en las mismas operaciones.
  async anticiposHipotesis(corridaId, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis`, token);
  },

  async anticiposProponerHipotesis(corridaId, body, token) {
    return request(`/anticipos/corridas/${corridaId}/hipotesis/proponer`, token, json("POST", body));
  },

  // Vista previa del archivo bancario: controles y totales, nunca un archivo descargable.
  async vistaPreviaBancaria(corridaId, { hipotesisIds = [], pagosManuales = [], txtHistorico = null }, token) {
    const formData = new FormData();
    hipotesisIds.forEach((id) => formData.append("hipotesisIds", id));
    pagosManuales.forEach((id) => formData.append("pagosManuales", id));
    if (txtHistorico) formData.append("txtHistorico", txtHistorico);
    return request(`/anticipos/corridas/${corridaId}/vista-previa-bancaria`, token, { method: "POST", body: formData });
  },

  async anticiposSimular(reglaId, corridaId, token) {
    return request(`/anticipos/reglas/${reglaId}/simular`, token, json("POST", { corridaId }));
  },

  async anticiposVerificarSimulacion(reglaId, token) {
    return request(`/anticipos/reglas/${reglaId}/recalcular`, token);
  },

  async anticiposEnviar(reglaId, token) {
    return request(`/anticipos/reglas/${reglaId}/enviar`, token, json("POST", {}));
  },

  async anticiposAprobar(reglaId, comentario, token) {
    return request(`/anticipos/reglas/${reglaId}/aprobar`, token, json("POST", { comentario }));
  },

  async anticiposRechazar(reglaId, comentario, token) {
    return request(`/anticipos/reglas/${reglaId}/rechazar`, token, json("POST", { comentario }));
  },

  async furSimular({ importacionId, snapshotId, diario }, token) {
    const formData = new FormData();
    formData.append("importacionId", importacionId);
    formData.append("snapshotId", snapshotId);
    if (diario) formData.append("diario", diario);
    return request("/fur-autonomo/corridas", token, { method: "POST", body: formData });
  },
};

function json(method, body) {
  return { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) };
}
